// Colors the supplier no longer sells (Mike's SanMar K100 Lime / Pink Azalea,
// 2026-10-07).
//
// The product-detail payload lists them in `not_sellable_colors` (and leaves
// them out of `colors`). Jake's decisions:
//
// - Hidden from the editor completely, including ones a product already
//   holds: the product editor store filters them out of its effective
//   `selected_color_skus` / `selected_colors` (one read seam, so every panel
//   agrees).
// - Dropped on the editor's next save of that product, together with the
//   product's other per-color data for them, and a snackbar names what was
//   dropped and why.
// - Fail open: a payload that does not report sellability, or that belongs
//   to a different SKU than the one being edited, hides and drops nothing.
// - Never leave a product with no colors: when EVERY selected color is
//   unsellable nothing is hidden or dropped; the distributor is told instead.
//
// Names compare through foldColorName, a mirror of the backend's
// normalize_color_name_for_match (case, whitespace, the trailing SanMar `*`
// version marker, padding around a slash).

import { withoutColors } from '@/utils/storefrontImages'

export function foldColorName(name) {
    if (typeof name !== 'string' || !name) return ''
    let out = name.replace(/\s+$/, '')
    if (out.endsWith('*')) out = out.slice(0, -1)
    return out
        .replace(/\s+/g, ' ')
        .trim()
        .replace(/\s*\/\s*/g, '/')
        .toLowerCase()
}

/**
 * The folded names the supplier reports as not sellable for `sku`, or null
 * when that is not known (no `not_sellable_colors`, or the payload is for a
 * different SKU).
 */
export function notSellableColorKeys(meta, sku) {
    const rows = meta?.not_sellable_colors
    if (!Array.isArray(rows) || rows.length === 0) return null
    const metaSku = typeof meta?.sku === 'string' ? meta.sku.trim().toUpperCase() : ''
    const editedSku = typeof sku === 'string' ? sku.trim().toUpperCase() : ''
    if (metaSku && editedSku && metaSku !== editedSku) return null
    const keys = new Set()
    for (const row of rows) {
        const key = foldColorName(row?.color_sku)
        if (key) keys.add(key)
    }
    return keys.size > 0 ? keys : null
}

/**
 * The keys to hide for this selection: `keys`, unless no selected color
 * would remain (then null -- never empty a product's colors).
 */
export function unsellableKeysToHide(keys, selectedSkus) {
    if (!keys) return null
    const skus = Array.isArray(selectedSkus) ? selectedSkus : []
    const hit = skus.some((s) => keys.has(foldColorName(s)))
    if (!hit) return null
    const kept = skus.some((s) => !keys.has(foldColorName(s)))
    return kept ? keys : null
}

/** `selected_color_skus` or `selected_colors` without the hidden colors. */
export function withoutUnsellable(path, value, keys) {
    if (!keys || !Array.isArray(value)) return value
    if (path === 'selected_color_skus') {
        return value.filter((s) => !keys.has(foldColorName(s)))
    }
    if (path === 'selected_colors') {
        return value.filter((c) => !keys.has(foldColorName(c?.color_name)))
    }
    return value
}

function withoutKeys(map, keys) {
    if (!map || typeof map !== 'object' || Array.isArray(map)) return map
    const drop = Object.keys(map).filter((k) => keys.has(foldColorName(k)))
    if (drop.length === 0) return map
    const next = { ...map }
    for (const k of drop) delete next[k]
    return next
}

/**
 * What a save must change to drop the unsellable colors a product holds.
 *
 * `read(path)` returns the product's current (unfiltered) value. Returns
 * null when there is nothing to drop; `{ refused: true, colors }` when every
 * selected color is unsellable (nothing is dropped); otherwise
 * `{ dropped, changes }`, where `changes` maps each attribute that has to
 * change to its new value.
 */
export function planUnsellableColorDrop(read, keys) {
    if (!keys) return null
    const skus = read('selected_color_skus') ?? []
    const selectedColors = read('selected_colors') ?? []
    const dropped = []
    const seen = new Set()
    const names = [
        ...(Array.isArray(skus) ? skus : []),
        ...(Array.isArray(selectedColors) ? selectedColors.map((c) => c?.color_name) : []),
    ]
    for (const name of names) {
        const key = foldColorName(name)
        if (key && keys.has(key) && !seen.has(key)) {
            seen.add(key)
            dropped.push(name)
        }
    }
    if (dropped.length === 0) return null
    if (!unsellableKeysToHide(keys, skus)) {
        return { refused: true, colors: dropped }
    }

    const changes = {
        selected_color_skus: withoutUnsellable('selected_color_skus', skus, keys),
        selected_colors: withoutUnsellable('selected_colors', selectedColors, keys),
    }
    const images = read('storefront_images')
    if (images && typeof images === 'object') {
        const matched = Object.keys(images).filter((k) => keys.has(foldColorName(k)))
        if (matched.length) changes.storefront_images = withoutColors(images, matched)
    }
    for (const path of ['manual_ld', 'color_overrides', 'images']) {
        const current = read(path)
        const next = withoutKeys(current, keys)
        if (next !== current) changes[path] = next
    }
    if (keys.has(foldColorName(read('default_image')))) {
        changes.default_image = null
    }
    for (const path of ['selected_color_skus', 'selected_colors']) {
        const before = path === 'selected_color_skus' ? skus : selectedColors
        if (Array.isArray(before) && changes[path].length === before.length) delete changes[path]
    }
    return { dropped, changes }
}

/** The snackbar text for a drop, or for a refused one. */
export function unsellableColorMessage(colors, supplierName, refused = false) {
    const supplier = supplierName || 'The supplier'
    const list = colors.join(', ')
    const these = colors.length === 1 ? 'this color' : 'these colors'
    if (refused) {
        return `${supplier} no longer sells ${list}. Add a color ${supplier} sells; ${these} will then be removed.`
    }
    return `Removed ${list}: ${supplier} no longer sells ${these}.`
}
